import React from "react";
import {FormGroup, Input} from "reactstrap";
import {useTranslation} from "react-i18next";
import QButton from "../../../../common/components/QButton";

interface FieldRow {
    label: string;
    value: React.ReactNode;
    highlight?: boolean;
    fullWidth?: boolean;
}

interface Props {
    title: string;
    complete: boolean;
    onEdit: () => void;
    fields: FieldRow[];
    children?: React.ReactNode;
    /** When false, section is not in enabled_sync_modules (V2). */
    enabled?: boolean;
    onToggleEnabled?: (enabled: boolean) => void;
    toggling?: boolean;
    /**
     * Accounting mode blocks this module (e.g. Invoice under customer_balance_ledger).
     * Sync toggle is disabled; mappings can still be viewed/edited.
     */
    accountingBlocked?: boolean;
    accountingBlockedBadge?: string;
    accountingBlockedHelp?: string;
}

const SetupSectionCard: React.FC<Props> = ({
    title,
    complete,
    onEdit,
    fields,
    children,
    enabled = true,
    onToggleEnabled,
    toggling = false,
    accountingBlocked = false,
    accountingBlockedBadge,
    accountingBlockedHelp,
}) => {
    const {t} = useTranslation();

    const syncLabel = enabled ? t("economic.syncActive") : t("economic.syncInactive");

    const syncSwitch = onToggleEnabled ? (
        <div className="d-inline-flex align-items-center gap-2 flex-shrink-0 economic-sync-toggle">
            <FormGroup switch className="mb-0">
                <Input
                    type="switch"
                    role="switch"
                    checked={enabled && !accountingBlocked}
                    disabled={toggling || accountingBlocked}
                    onChange={() => {
                        if (accountingBlocked) {
                            return;
                        }
                        onToggleEnabled(!enabled);
                    }}
                    aria-label={t("economic.syncServiceLabel", {module: title})}
                />
            </FormGroup>
            <span className="small text-muted mb-0 user-select-none">
                {accountingBlocked ? t("economic.syncInactive") : syncLabel}
            </span>
        </div>
    ) : null;

    if (accountingBlocked) {
        return (
            <div className="business-detail bg-orange-bland-zp bdr-20 pd-25 mb-3 opacity-90">
                <div className="row align-items-center mb-2">
                    <div className="col-md-8 d-flex align-items-center gap-2 flex-wrap">
                        <h2 className="fw-bold fs-24 mb-0">{title}</h2>
                        <span className="badge bg-secondary">
                            {accountingBlockedBadge || t("economic.accountingModeSkippedBadge")}
                        </span>
                    </div>
                    <div className="col-md-4 text-end d-flex align-items-center justify-content-end gap-2 flex-wrap">
                        {syncSwitch}
                        <QButton
                            type="default"
                            className="btn-sm"
                            onClick={onEdit}
                            content={t("economic.edit")}
                        />
                    </div>
                </div>
                <div className="alert alert-warning py-2 mb-2" role="status">
                    {accountingBlockedHelp || t("economic.accountingModeInvoiceSkippedHelp")}
                </div>
                {children}
            </div>
        );
    }

    if (!enabled) {
        return (
            <div className="business-detail bg-orange-bland-zp bdr-20 pd-25 mb-3 opacity-90">
                <div className="row align-items-center">
                    <div className="col-md-8">
                        <h2 className="fw-bold fs-24 mb-1">{title}</h2>
                        <p className="text-muted mb-0">{t("economic.moduleDisabledHelp")}</p>
                    </div>
                    <div className="col-md-4 text-end d-flex align-items-center justify-content-end gap-2 flex-wrap">
                        <span className="badge bg-secondary">{t("economic.moduleDisabled")}</span>
                        {syncSwitch}
                        <QButton
                            type="default"
                            className="btn-sm"
                            onClick={onEdit}
                            content={t("economic.edit")}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="business-detail bg-orange-bland-zp bdr-20 pd-25 mb-3">
            <div className="row align-items-center mb-2">
                <div className="col-md-8 d-flex align-items-center gap-2 flex-wrap">
                    <h2 className="fw-bold fs-24 mb-0">{title}</h2>
                    <span className={`badge ${complete ? "bg-success" : "bg-warning text-dark"}`}>
                        {complete ? t("economic.setupComplete") : t("economic.setupIncomplete")}
                    </span>
                </div>
                <div className="col-md-4 text-end d-flex align-items-center justify-content-end gap-2 flex-wrap">
                    {syncSwitch}
                    <QButton
                        type="default"
                        className="btn-sm"
                        onClick={onEdit}
                        content={t("economic.edit")}
                    />
                </div>
            </div>
            {children}
            <div className="row">
                {fields.map((field, index) => (
                    <div
                        key={`${field.label}-${index}`}
                        className={`${field.fullWidth ? "col-12" : "col-md-4"} mb-2`}
                    >
                        <b className={field.highlight ? "text-warning" : "text-danger"}>
                            {field.label}:
                        </b>{" "}
                        <span className={!field.value || field.value === t("economic.notSet") ? "text-muted fst-italic" : ""}>
                            {field.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default React.memo(SetupSectionCard);
